import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS = {
  idle: "Tap the orb to speak",
  listening: "Listening — tap to send",
  thinking: "Thinking...",
  speaking: "Concierge speaking",
};

export default function VoiceOrb({ sessionId, onClose }) {
  const [state, setState] = useState("idle");
  const [level, setLevel] = useState(0);
  const [transcript, setTranscript] = useState("");
  const [reply, setReply] = useState("");
  const recRef = useRef(null);
  const audioCtxRef = useRef(null);
  const rafRef = useRef(null);
  const audioRef = useRef(null);
  const greetedRef = useRef(false);

  const speakText = async (text) => {
    try {
      const ttsRes = await fetch(`${API}/tts`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text: text.slice(0, 1000) }),
      });
      if (!ttsRes.ok) {
        setState("idle");
        return;
      }
      const audioBlob = await ttsRes.blob();
      const audio = new Audio(URL.createObjectURL(audioBlob));
      audioRef.current = audio;
      setState("speaking");
      audio.onended = () => setState("idle");
      audio.play();
    } catch {
      setState("idle");
    }
  };

  useEffect(() => {
    if (greetedRef.current) return undefined;
    greetedRef.current = true;
    let already = false;
    try {
      already = !!sessionStorage.getItem("alkyrvis_orb_greeted");
      sessionStorage.setItem("alkyrvis_orb_greeted", "1");
    } catch {
      /* storage unavailable */
    }
    if (already) return undefined;
    let first = null;
    try {
      const name = localStorage.getItem("alkyrvis_name");
      first = name ? name.split(" ")[0] : null;
    } catch {
      /* no stored name */
    }
    const greeting = first
      ? `Welcome back, ${first}. What shall we automate today?`
      : "Hello. I am the Alkyrvis Concierge. Tap the orb and tell me what you would like to automate.";
    setReply(greeting);
    const t = setTimeout(() => speakText(greeting), 600);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(
    () => () => {
      stopVisuals();
      if (audioRef.current) audioRef.current.pause();
    },
    []
  );

  const stopVisuals = () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    if (audioCtxRef.current) {
      audioCtxRef.current.close().catch(() => {});
      audioCtxRef.current = null;
    }
    setLevel(0);
  };

  const startListening = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      const chunks = [];
      rec.ondataavailable = (e) => chunks.push(e.data);
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        stopVisuals();
        const blob = new Blob(chunks, { type: rec.mimeType || "audio/webm" });
        process(blob);
      };
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const src = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 256;
      src.connect(analyser);
      audioCtxRef.current = ctx;
      const data = new Uint8Array(analyser.frequencyBinCount);
      const loop = () => {
        analyser.getByteFrequencyData(data);
        setLevel(data.reduce((a, b) => a + b, 0) / data.length / 255);
        rafRef.current = requestAnimationFrame(loop);
      };
      loop();
      recRef.current = rec;
      rec.start();
      setState("listening");
    } catch {
      setState("idle");
    }
  };

  const process = async (blob) => {
    setState("thinking");
    try {
      const fd = new FormData();
      fd.append("file", blob, "voice.webm");
      const sttRes = await fetch(`${API}/stt`, { method: "POST", body: fd });
      const sttJson = await sttRes.json();
      const text = (sttJson.text || "").trim();
      if (!text) {
        setState("idle");
        return;
      }
      setTranscript(text);

      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, message: text }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      let full = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const p of parts) {
          const line = p.trim();
          if (!line.startsWith("data:")) continue;
          const data = line.slice(5).trim();
          if (data === "[DONE]") continue;
          try {
            const j = JSON.parse(data);
            if (j.delta) full += j.delta;
          } catch {
            /* partial */
          }
        }
      }
      if (!full.trim()) {
        setState("idle");
        return;
      }
      setReply(full.trim());
      speakText(full.trim());
    } catch {
      setState("idle");
    }
  };

  const onTap = () => {
    if (state === "idle") startListening();
    else if (state === "listening" && recRef.current) recRef.current.stop();
  };

  return (
    <motion.div
      data-testid="voice-orb-overlay"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
      className="fixed inset-0 z-[90] flex flex-col items-center justify-center overflow-hidden bg-[#050505]/95 px-6 backdrop-blur-xl"
    >
      <div className="grid-lines absolute inset-0 opacity-40 [mask-image:radial-gradient(ellipse_60%_60%_at_50%_50%,black,transparent)]" />
      <button
        type="button"
        data-testid="voice-orb-close"
        onClick={onClose}
        className="absolute right-6 top-6 text-[#A3A3A3] transition-colors duration-300 hover:text-gold"
        aria-label="Close voice mode"
      >
        <X className="h-6 w-6" />
      </button>

      <p className="relative font-mono text-[11px] uppercase tracking-[0.45em] text-gold">
        Alkyrvis Concierge — Voice Mode
      </p>

      <motion.button
        type="button"
        data-testid="voice-orb-button"
        onClick={onTap}
        whileTap={{ scale: 0.94 }}
        className="relative mt-14 h-60 w-60 rounded-full md:h-72 md:w-72"
        aria-label="Tap to speak"
      >
        <div
          className="absolute -inset-10 rounded-full bg-[radial-gradient(circle,rgba(216,192,138,0.35),transparent_70%)] blur-3xl transition-[transform,opacity] duration-150"
          style={{
            transform: `scale(${1 + level * 1.6})`,
            opacity: state === "listening" ? 0.95 : 0.5,
          }}
        />
        <motion.div
          className="absolute inset-0 rounded-full border border-gold/30"
          animate={
            state === "speaking"
              ? { scale: [1, 1.14, 1], opacity: [0.6, 1, 0.6] }
              : state === "thinking"
                ? { scale: [1, 1.07, 1], opacity: [0.4, 0.9, 0.4] }
                : { scale: [1, 1.04, 1] }
          }
          transition={{
            duration: state === "speaking" ? 0.9 : state === "thinking" ? 1.1 : 4.5,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
        <div className="absolute inset-3 rounded-full border border-dashed border-gold/20 animate-spin-slower" />
        <div
          className="absolute inset-0 rounded-full bg-[radial-gradient(circle_at_35%_30%,rgba(216,192,138,0.28),rgba(5,5,5,0.92)_62%)] shadow-[inset_0_0_60px_rgba(216,192,138,0.18),0_0_90px_rgba(216,192,138,0.28)] transition-transform duration-150"
          style={{ transform: `scale(${1 + level * 0.28})` }}
        />
        <img
          src="/assets/emblem.png"
          alt=""
          className="absolute inset-0 m-auto h-24 w-24 object-contain drop-shadow-[0_0_30px_rgba(216,192,138,0.75)]"
        />
      </motion.button>

      <p
        data-testid="voice-orb-status"
        className={`relative mt-12 font-mono text-xs uppercase tracking-[0.35em] ${
          state === "listening" ? "text-mint" : "text-[#A3A3A3]"
        }`}
      >
        {STATUS[state]}
      </p>

      <div className="relative mt-8 max-w-lg space-y-3 text-center">
        {transcript && (
          <p data-testid="voice-orb-transcript" className="text-sm text-gold/80">
            You: {transcript}
          </p>
        )}
        {reply && (
          <p data-testid="voice-orb-reply" className="text-sm leading-relaxed text-[#C9C9C9]">
            {reply}
          </p>
        )}
      </div>
    </motion.div>
  );
}
